import { Component, Input, ViewChild, OnInit, OnChanges, SimpleChanges, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { BarreOngletsComponent, DonneesColonne, DonneesColonneBoutonIconeClick, DonneesMenuItem, DonneesOnglet, FonteIconeComponent, GrilleComponent, SimpleLibelleComponent, SimplePanelComponent, TelechargementFichier, AttenteOperationComponent, DonneesGrilleSelection } from '../../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import { RetournerVueVenteResultat } from '../../../RetournerVueVenteResultat';
import { EFactVenteService } from '../../../val-efact-vente.service';
import { TranslocoCurrencyPipe, TranslocoDatePipe } from '@jsverse/transloco-locale';
import { RetournerVueVenteCycleVieResultat, RetournerVueVenteDocumentResultat, RetournerVueVenteEncaissementsResultat, ValidationError } from '../../../../public-api';
import { GererHabilitationsMenuItems, PermissionsService } from '../../../../../../../val-ammon-permissions/val-permissions-lib/src/public-api';
import { PDFViewerModule } from '@progress/kendo-angular-pdfviewer';
import { ModifierStatutDialogueComponent } from './modifier-statut-dialogue.component';

// Composant contenu réutilisable pour la fiche de facture de vente
// Utilisé à la fois dans le modal (fiche générique) et dans la page dédiée
@Component(
	{
		selector: 'val-facture-vente-fiche-contenu',
		imports: [
			TranslocoModule,
			SimplePanelComponent,
			SimpleLibelleComponent,
			FonteIconeComponent,
			TranslocoDatePipe,
			TranslocoCurrencyPipe,
			GrilleComponent,
			BarreOngletsComponent,
			AttenteOperationComponent,
			PDFViewerModule,
			ModifierStatutDialogueComponent
		],
		templateUrl: './vue-facture-vente-fiche-contenu.component.html',
		styleUrls: ['./vue-facture-vente-fiche-contenu.component.scss']
	})
export class VueFactureVenteFicheContenuComponent implements OnInit {

	// ID de la vente
	private venteId!: string | number;

	// Barre d'onglets
	@ViewChild("barreOnglets")
	public barreOnglets!: BarreOngletsComponent;

	// Grille de l'historique
	@ViewChild("grilleHistorique")
	public grilleHistorique!: GrilleComponent;

	// Grille des encaissements
	@ViewChild("grilleEncaissements")
	public grilleEncaissements!: GrilleComponent;

	// Boîte de dialogue de modification manuelle du statut
	@ViewChild("modifierStatutDialogue")
	public modifierStatutDialogue!: ModifierStatutDialogueComponent;

	// Onglets
	public onglets: DonneesOnglet[];

	// Données de consultation
	public donnees: RetournerVueVenteResultat | null;

	// Colonnes historique cycle de vie
	public colonnes_historique: DonneesColonne[];

	// Données historique cycle de vie
	public donnees_historique: RetournerVueVenteCycleVieResultat[];

	// Colonnes de détail des erreurs de conformité
	public colonnes_erreurs_conformite: DonneesColonne[];

	// Détail des erreurs de conformité affichées
	public donnees_erreurs_conformite: ValidationError[];

	// Indique si le panel des erreurs de conformité est affiché
	public afficherErreursConformite: boolean;

	// Indique si le panel "Retour de l'acheteur" est affiché
	public afficherRetourAcheteur: boolean;

	// Ligne historique sélectionnée avec un motif ou une note
	public retourAcheteurSelectionne: RetournerVueVenteCycleVieResultat | null;

	// Colonnes encaissements
	public colonnes_encaissements: DonneesColonne[];

	// Données encaissements
	public donnees_encaissements: RetournerVueVenteEncaissementsResultat[];

	// Encaissement sélectionné dans la grille (avec une erreur de transmission)
	public encaissementSelectionneErreur: RetournerVueVenteEncaissementsResultat | null;

	// Colonnes documents liés à la vente
	public colonnes_documents: DonneesColonne[];

	// Données documents liés à la vente
	public donnees_documents: RetournerVueVenteDocumentResultat[];

	// Indique si une opération est en cours
	public operationEnCours: boolean;

	public menuItems: DonneesMenuItem[];

	private ongletDetails: DonneesOnglet;
	private ongletEncaissements: DonneesOnglet;
	private ongletDocuments: DonneesOnglet;

	// Clés de traduction des libellés de colonnes, par identifiant de colonne
	private static readonly ClesColonnesHistorique: Record<string, string> = {
		"1": "vente.commun.statut",
		"2": "vente.fiche-contenu.historique.colonne-recu-le",
		"3": "vente.commun.motif",
		"4": "vente.fiche-contenu.historique.colonne-erreurs-conformite"
	};
	private static readonly ClesColonnesErreursConformite: Record<string, string> = {
		"1": "vente.fiche-contenu.erreurs-conformite.colonne-code",
		"2": "vente.commun.description",
		"3": "vente.fiche-contenu.erreurs-conformite.colonne-chemin"
	};
	private static readonly ClesColonnesEncaissements: Record<string, string> = {
		"1": "vente.fiche-contenu.encaissements.colonne-date-reglement",
		"2": "vente.fiche-contenu.encaissements.colonne-montant-transmis",
		"3": "vente.fiche-contenu.encaissements.colonne-date-etat-transmission",
		"4": "vente.fiche-contenu.encaissements.colonne-etat-transmission-pa"
	};
	private static readonly ClesColonnesDocuments: Record<string, string> = {
		"1": "vente.fiche-contenu.documents.colonne-libelle",
		"2": "vente.fiche-contenu.documents.colonne-type",
		"3": "vente.fiche-contenu.documents.colonne-date-creation",
		"4": "vente.fiche-contenu.documents.colonne-telecharger"
	};

	// Document sélectionné dans la grille
	public documentSelectionne: RetournerVueVenteDocumentResultat | null;

	// URL du document sélectionné pour le viewer PDF
	public urlDocumentSelectionne: string | undefined;

	// Nom du fichier du document sélectionné pour le viewer PDF
	public nomFichierDocumentSelectionne: string;

	// Création
	constructor(private eFactVenteService: EFactVenteService, private permissionsService: PermissionsService,
				private translocoService: TranslocoService, private destroyRef: DestroyRef) {
		this.donnees = new RetournerVueVenteResultat();
		this.colonnes_historique = [];
		this.colonnes_erreurs_conformite = [];
		this.colonnes_encaissements = [];
		this.colonnes_documents = [];
		this.donnees_historique = [];
		this.donnees_erreurs_conformite = [];
		this.donnees_encaissements = [];
		this.encaissementSelectionneErreur = null;
		this.donnees_documents = [];
		this.menuItems = [];
		this.operationEnCours = false;
		this.afficherErreursConformite = false;
		this.afficherRetourAcheteur = false;
		this.retourAcheteurSelectionne = null;
		this.documentSelectionne = null;
		this.urlDocumentSelectionne = undefined;
		this.nomFichierDocumentSelectionne = '';

		// Libellés des onglets posés par TraduireLibelles, dès que les traductions sont chargées
		this.ongletDetails = new DonneesOnglet();
		this.ongletDetails.identifiant = 'details';

		this.ongletEncaissements = new DonneesOnglet();
		this.ongletEncaissements.identifiant = 'encaissements';

		this.ongletDocuments = new DonneesOnglet();
		this.ongletDocuments.identifiant = 'documents';

		this.onglets = [this.ongletDetails, this.ongletEncaissements, this.ongletDocuments];
	}

	// Initialise le composant
	public async ngOnInit(): Promise<void> {
		this.CreerColonnes();
		this.CreerColonnesErreursConformite();
		this.CreerColonnesEncaissements();
		this.CreerColonnesDocuments();
		this.CreerMenuItems();

		// Émet une fois les traductions de la langue active chargées, puis à chaque changement de langue
		this.translocoService.selectTranslation()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.TraduireLibelles());
	}

	// Traduit les libellés des onglets et des colonnes dans la langue active
	//  Les colonnes sont modifiées en place : la grille y conserve la taille et l'ordre choisis par l'utilisateur
	private TraduireLibelles(): void {
		this.TraduireOnglets();
		this.TraduireColonnes(this.colonnes_historique, VueFactureVenteFicheContenuComponent.ClesColonnesHistorique);
		this.TraduireColonnes(this.colonnes_erreurs_conformite, VueFactureVenteFicheContenuComponent.ClesColonnesErreursConformite);
		this.TraduireColonnes(this.colonnes_encaissements, VueFactureVenteFicheContenuComponent.ClesColonnesEncaissements);
		this.TraduireColonnes(this.colonnes_documents, VueFactureVenteFicheContenuComponent.ClesColonnesDocuments);
	}

	// Traduit les libellés des onglets ; celui des documents porte leur nombre s'il y en a
	private TraduireOnglets(): void {
		this.ongletDetails.libelle = this.translocoService.translate('vente.fiche-contenu.onglet-details');
		this.ongletEncaissements.libelle = this.translocoService.translate('vente.fiche-contenu.onglet-encaissements');

		if (this.donnees_documents.length > 0)
			this.ongletDocuments.libelle = this.translocoService.translate('vente.fiche-contenu.onglet-documents-nombre', { nb: this.donnees_documents.length });
		else
			this.ongletDocuments.libelle = this.translocoService.translate('vente.fiche-contenu.onglet-documents');
	}

	// Traduit les libellés d'une liste de colonnes
	private TraduireColonnes(colonnes: DonneesColonne[], cles: Record<string, string>): void {
		for (const colonne of colonnes)
			colonne.libelle = this.translocoService.translate(cles[colonne.identifiant]);
	}

	// Appelé après la création du composant pour charger les données
	public async ChargerDonnees(venteId: string | number): Promise<void> {
		this.venteId = venteId;

		this.ResetDonnees();
		this.ResetOnglets();
		this.ResetViewerPDF();

		await this.RecupererDonnees();
		await this.CreerMenuItems();
		await this.RecupererHistorique();
		this.RecupererEncaissements();  // chargement en arrière plan
		this.RecupererDocuments();  // chargement en arrière plan
	}

	private ResetDonnees(): void {
		this.donnees = new RetournerVueVenteResultat();
		this.donnees_historique = [];
		this.donnees_erreurs_conformite = [];
		this.donnees_encaissements = [];
		this.encaissementSelectionneErreur = null;
		this.donnees_documents = [];
		this.afficherErreursConformite = false;
		this.afficherRetourAcheteur = false;
		this.retourAcheteurSelectionne = null;
	}

	private ResetOnglets(): void {
		this.barreOnglets.SelectionnerOnglet(this.onglets[0]);

		// Appelé après ResetDonnees : sans document chargé, l'onglet reprend son libellé sans nombre
		this.TraduireOnglets();
	}

	async RecupererDonnees() {
		try {
			let valApiResultat = await this.eFactVenteService.RetournerVueVente(this.venteId);
			this.donnees = valApiResultat.resultat;

		} catch {
		}
	}

	async RecupererHistorique() {
		try {
			let valApiResultat = await this.eFactVenteService.RetournerVueHistoriqueCycleVieVente(this.venteId);
			if (valApiResultat.status === "success" && valApiResultat.resultat)
				this.donnees_historique = valApiResultat.resultat;

			this.SelectionnerPremiereLigneHistorique();
		} catch {
		}
	}

	async RecupererEncaissements() {
		try {
			let valApiResultat = await this.eFactVenteService.RetournerVueEncaissementsVente(this.venteId);
			if (valApiResultat.status === "success" && valApiResultat.resultat) {
				this.donnees_encaissements = valApiResultat.resultat;
				this.donnees_encaissements.sort((a, b) => new Date(b.datePaiement).getTime() - new Date(a.datePaiement).getTime());
			}

			this.SelectionnerPremiereLigneEncaissements();
		} catch {
		}
	}

	async RecupererDocuments() {
		try {
			let valApiResultat = await this.eFactVenteService.RetournerVueDocumentsLiesVente(this.venteId);
			if (valApiResultat.status === "success" && valApiResultat.resultat)
				this.donnees_documents = valApiResultat.resultat;

			this.TraduireOnglets();
		} catch {
		}
	}

	private CreerColonnesEncaissements(): void {
		this.colonnes_encaissements =
			[
				{ identifiant: "1", champ: "datePaiement", libelle: "", format: "DAT", taille: 150, visible: true },
				{ identifiant: "2", champ: "montantRegle", libelle: "", format: "CUR_SIGN", taille: 150, visible: true },
				{ identifiant: "3", champ: "etatTransmissionDate", libelle: "", format: "DAT_H", taille: 180, visible: true },
				{ identifiant: "4", champ: "etatTransmissionPa", libelle: "", format: "TXT_COLOR", taille: 180, visible: true }
			];
	}

	// Crée les colonnes historique cycle de vie (libellés posés par TraduireLibelles)
	private CreerColonnes(): void {
		this.colonnes_historique =
			[
				{ identifiant: "1", champ: "statutEtCodeCouleur", libelle: "", format: "TXT_COLOR", taille: 150, visible: true, couleurParDefaut: "#6D7377" },
				{ identifiant: "2", champ: "dateReception", libelle: "", format: "DAT_H", taille: 150, visible: true },
				{ identifiant: "3", champ: "existeMotifOuNote", libelle: "", format: "CHK", taille: 70, visible: true },
				{ identifiant: "4", champ: "existeErreursConformite", libelle: "", format: "CHK", taille: 70, visible: true }
			];
	}

	private CreerColonnesErreursConformite(): void {
		this.colonnes_erreurs_conformite =
			[
				{ identifiant: "1", champ: "codeRegle", libelle: "", format: "TXT_COLOR", taille: 80, visible: true, couleurParDefaut: "#b5141e" },
				{ identifiant: "2", champ: "description", libelle: "", format: "TXT", taille: 290, visible: true },
				{ identifiant: "3", champ: "chemin", libelle: "", format: "TXT", taille: 420, visible: true }
			];
	}

	private CreerColonnesDocuments(): void {
		this.colonnes_documents =
			[
				{ identifiant: "1", champ: "libelle", libelle: "", format: "TXT", taille: 150, visible: true },
				{ identifiant: "2", champ: "typeDocument", libelle: "", format: "TXT", taille: 100, visible: true },
				{ identifiant: "3", champ: "dateCreation", libelle: "", format: "DAT", taille: 70, visible: true },
				{ identifiant: "4", champ: "iconTelecharger", libelle: "", format: "BTN_ICON", taille: 50, visible: true },
			];
	}

	public DocumentSelectionChange(selection: DonneesGrilleSelection): void {
		if (selection && selection.identifiantFocus) {
			const documentId = selection.identifiantFocus;
			const document = this.donnees_documents.find(doc => doc.id === documentId) || null;

			if (document) {
				// Charger le document pour l'afficher dans le viewer
				this.eFactVenteService.RetournerDocumentVente(this.venteId, documentId).then(resultat => {
					if (resultat.status === "success" && resultat.resultat && resultat.resultat.typeContenu === "application/pdf") {
						const blob = TelechargementFichier.CreerBlob(resultat.resultat.contenu, resultat.resultat.typeContenu);

						this.documentSelectionne = document;
						this.urlDocumentSelectionne = URL.createObjectURL(blob);
						this.nomFichierDocumentSelectionne = resultat.resultat.nomFichier;
					}
					else {
						this.ResetViewerPDF();
					}
				});
			}
		} else {
			this.ResetViewerPDF();
		}
	}

	public EncaissementSelectionChange(selection: DonneesGrilleSelection): void {
		if (!selection || !selection.identifiantFocus) {
			this.encaissementSelectionneErreur = null;
			return;
		}

		const encaissement = this.donnees_encaissements.find(e => e.id === selection.identifiantFocus) || null;
		this.encaissementSelectionneErreur = encaissement?.transmissionErreurCode ? encaissement : null;
	}

	public HistoriqueSelectionChange(selection: DonneesGrilleSelection): void {
		if (!selection || !selection.identifiantFocus) {
			this.MasquerErreursConformiteHistorique();
			this.MasquerRetourAcheteurHistorique();
			return;
		}

		const historique = this.donnees_historique.find(ligne => ligne.id === selection.identifiantFocus);

		if (historique && this.ADesErreursConformite(historique))
			this.AfficherErreursConformiteHistorique(historique);
		else
			this.MasquerErreursConformiteHistorique();

		if (historique && this.ADesMotifOuNote(historique))
			this.AfficherRetourAcheteurHistorique(historique);
		else
			this.MasquerRetourAcheteurHistorique();
	}

	public DocumentColonneBoutonIconeClick(evenement: DonneesColonneBoutonIconeClick): void {
		const documentId = evenement.identifiant;

		this.eFactVenteService.RetournerDocumentVente(this.venteId, documentId).then(resultat => {
			if (resultat.status === "success" && resultat.resultat)
				TelechargementFichier.Telecharger(resultat.resultat.nomFichier, resultat.resultat.contenu, resultat.resultat.typeContenu);
		});
	}

	// Crée le menu
	private async CreerMenuItems(): Promise<void> {
		let menuItems: DonneesMenuItem[] = [];
		let gererHabilitationsMenuItems: GererHabilitationsMenuItems;


		if (this.donnees?.estStatutModifiable) {
			const menuModifierStatut: DonneesMenuItem = { dico: "F13FFF9C-5A9F-4B2B-BFFA-F1ED32CB3271", iconeClass: "fas fa-sign-in-alt", action: this.OuvrirModifierStatut.bind(this) };
			menuItems.push(menuModifierStatut);
		}

		const menuTelechargerTout: DonneesMenuItem = { dico: "8D8607CD-EB6C-49C6-B539-EBCE5512359D", iconeClass: "fas fa-file-archive", action: this.TelechargerTous.bind(this) };
		const menuPrevisualiser: DonneesMenuItem = { dico: "A8B33816-0307-4CD2-A260-48FC9AC9CD97", iconeClass: "fas fa-eye", action: this.PrevisualiserFactureElectronique.bind(this) };

		menuItems.push(menuPrevisualiser);
		menuItems.push(menuTelechargerTout);

		gererHabilitationsMenuItems = new GererHabilitationsMenuItems(menuItems, this.permissionsService);

		this.menuItems = await gererHabilitationsMenuItems.Executer();
	}

	// Ouvre la boîte de dialogue de modification manuelle du statut, puis rafraîchit la fiche si nécessaire
	private async OuvrirModifierStatut(): Promise<void> {
		const doitRafraichir = await this.modifierStatutDialogue.Ouvrir(this.venteId);

		if (doitRafraichir) {
			await this.RecupererDonnees();
			await this.CreerMenuItems();
			await this.RecupererHistorique();
			this.RecupererEncaissements();  
			this.RecupererDocuments();  
		}
	}

	// Télécharge en priorité le document "Facture/Avoir électronique" au format PDF.
	// Si aucun document de ce type n'existe, génère la facture électronique à la volée.
	private async PrevisualiserFactureElectronique(): Promise<void> {
		const TYPE_EFAC_ID = 'b4726ed0-7446-4e7a-ae91-7ce9a3b322f3';
		const documentsFacture = this.donnees_documents.filter(d => d.typeDocumentId?.toLowerCase() === TYPE_EFAC_ID);

		let resultat;
		if (documentsFacture.length) {
			const documentPdf = documentsFacture.find(d => d.extension?.toLowerCase() === '.pdf');
			const doc = documentPdf ?? documentsFacture[0];
			resultat = await this.eFactVenteService.RetournerDocumentVente(this.venteId, doc.id);
		} else {
			resultat = await this.eFactVenteService.GenererEFacture(this.venteId);
			if (resultat.status === "success")
				this.RecupererDocuments();  // rafraîchissement en arrière-plan
		}

		if (resultat.status === "success" && resultat.resultat)
			TelechargementFichier.Telecharger(resultat.resultat.nomFichier, resultat.resultat.contenu, resultat.resultat.typeContenu);
	}

	// Télécharge tous les documents liés à la vente dans une archive zip
	private TelechargerTous(): void {
		this.eFactVenteService.RetournerDocumentsVenteZip(this.venteId).then(resultat => {
			if (resultat.status === "success" && resultat.resultat)
				TelechargementFichier.Telecharger(resultat.resultat.nomFichier, resultat.resultat.contenu, resultat.resultat.typeContenu);
		});
	}

	// Réinitialise le viewer PDF
	private ResetViewerPDF(): void {
		this.documentSelectionne = null;
		this.urlDocumentSelectionne = undefined;
		this.nomFichierDocumentSelectionne = '';
	}

	private ADesErreursConformite(historique: RetournerVueVenteCycleVieResultat | null | undefined): boolean {
		return !!historique && historique.existeErreursConformite;
	}	

	private AfficherErreursConformiteHistorique(historique: RetournerVueVenteCycleVieResultat): void {
		this.donnees_erreurs_conformite = historique.erreursConformite;
		this.afficherErreursConformite = true;
	}

	private MasquerErreursConformiteHistorique(): void {
		this.donnees_erreurs_conformite = [];
		this.afficherErreursConformite = false;
	}

	private ADesMotifOuNote(historique: RetournerVueVenteCycleVieResultat | null | undefined): boolean {
		return !!historique && historique.existeMotifOuNote;
	}

	private AfficherRetourAcheteurHistorique(historique: RetournerVueVenteCycleVieResultat): void {
		this.retourAcheteurSelectionne = historique;
		this.afficherRetourAcheteur = true;
	}

	private MasquerRetourAcheteurHistorique(): void {
		this.retourAcheteurSelectionne = null;
		this.afficherRetourAcheteur = false;
	}

	public ParseNote(note: string): string[] {
		try
		{
			const parsed = JSON.parse(note);
			if (Array.isArray(parsed))
				return parsed;
		} catch
		{
			// Si la note n'est pas sous forme de tableau de plusieurs notes
			// on l'affiche tel quel
		}
		return [note];
	}

	private SelectionnerPremiereLigneEncaissements(): void {
		if (!this.donnees_encaissements.length)
			return;

		// Attend le prochain cycle pour garantir la disponibilité de la grille et des données Kendo.
		setTimeout(() => {
			const premierEncaissement = this.donnees_encaissements[0];

			if (!premierEncaissement || !this.grilleEncaissements)
				return;

			this.grilleEncaissements.donneesSelection.identifiantFocus = premierEncaissement.id;
			this.grilleEncaissements.SelectionChange();
		});
	}

	private SelectionnerPremiereLigneHistorique(): void {
		if (!this.donnees_historique.length)
			return;

		// Attend le prochain cycle pour garantir la disponibilité de la grille et des données Kendo.
		setTimeout(() => {
			const premierHistorique = this.donnees_historique[0];

			if (!premierHistorique || !this.grilleHistorique)
				return;

			this.grilleHistorique.donneesSelection.identifiantFocus = premierHistorique.id;
			this.grilleHistorique.SelectionChange();
		});
	}

	// Changement d'onglet
	public OngletSelectionneChange(onglet: DonneesOnglet): void {
		this.ResetViewerPDF();

		if (onglet.identifiant === 'details')
			this.SelectionnerPremiereLigneHistorique();
		else if (onglet.identifiant === 'encaissements')
			this.SelectionnerPremiereLigneEncaissements();
	}

	// Rafraîchissement de l'onglet
	public async RafraichissementOngletDemande(onglet: DonneesOnglet): Promise<void> {
		this.operationEnCours = true;

		if (onglet.identifiant === 'details') {
			await this.RecupererDonnees();
			await this.RecupererHistorique();
		}
		else if (onglet.identifiant === 'encaissements') {
			await this.RecupererEncaissements();
		}
		else if (onglet.identifiant === 'documents') {
			await this.RecupererDocuments();
			this.ResetViewerPDF();
		}

		this.operationEnCours = false;
	}

	// Indique s'il n'y a aucune donnée à afficher
	public AucuneDonnee(): boolean {
		return !this.donnees;
	}
}
